<template>
  <CardWrapper
    :title="i18n.crypto_chart_title()"
    class="crypto-chart-widget"
    data-test-id="crypto-chart-widget"
  >
    <div
      v-if="allowedBalances.length"
      class="crypto-chart-widget__container"
    >
      <div class="crypto-chart-widget__plot">
        <canvas
          ref="canvas"
          class="crypto-chart-widget__canvas"
          data-test-id="crypto-chart-widget-canvas"
        ></canvas>
      </div>
      <div class="crypto-chart-widget__legend" data-test-id="crypto-chart-widget-legend">
        <button
          v-for="item in legendItems"
          :key="item.asset"
          type="button"
          class="crypto-chart-widget__legend-item"
          :class="{ 'is-hidden': hiddenLegendItems[item.index] }"
          @click="toggleSector(item.index)"
        >
          <span class="crypto-chart-widget__legend-color" :style="{ backgroundColor: item.color }"></span>
          <span>{{ item.label }}</span>
        </button>
      </div>
    </div>
    <div
      v-else
      class="crypto-chart-widget__empty"
      data-test-id="crypto-chart-widget-empty"
    >
      <md-icon data-test-id="crypto-chart-widget-empty-icon">
        wallet
      </md-icon>
      <p data-test-id="crypto-chart-widget-empty-msg">
        {{ i18n.th_no_assets_in_your_wallet() }}
      </p>
    </div>
  </CardWrapper>
</template>

<script>
  import { i18n } from '@js/i18n'
  import { Doughnut } from 'vue-chartjs'
  import Chart from 'chart.js'
  import { mapGetters } from 'vuex'
  import { vuexTypes } from '@vuex/types'

  import CardWrapper from '@com/UI/CardWrapper.vue'
  import { PREFERRED_ASSET_CODES, formatTokenCode } from '@utils/consts'
  import CurrencyRound from '@com/mixins/currency.round.mixin'

  const centerTextPlugin = {
    beforeDraw (chart) {
      const ctx = chart.chart.ctx
      const { chartArea } = chart.chart

      const text = chart.config.data.centerText
      if (!text) return

      ctx.save()

      ctx.font = '600 20px Arial'
      ctx.fillStyle = '#000'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'

      // Точний центр
      const centerX = (chartArea.left + chartArea.right) / 2
      const centerY = (chartArea.top + chartArea.bottom) / 2

      ctx.fillText(text, centerX, centerY)

      ctx.restore()
    }
  }

  Chart.plugins.register(centerTextPlugin)

  export default {
    name: 'CryptoChart',
    components: { CardWrapper },
    extends: Doughnut,
    mixins: [CurrencyRound],
    props: {},
    data () {
      return {
        i18n,
        hiddenLegendItems: {},
        chartOptions: {
          responsive: true,
          maintainAspectRatio: false,
          cutoutPercentage: 90, // Тонкіше кільце
          rotation: -0.5 * Math.PI,
          legend: { display: false },
          tooltips: {
            enabled: false, // за замовчуванням відключаємо, рендеримо кастомний
            custom (tooltip) {
              // Форматуємо число від 2 до 6 знаків після коми
              function formatAmount (value) {
                if (value === null || value === undefined) return ''

                // Переводимо в число, якщо рядок
                const num = Number(value)
                if (isNaN(num)) return value

                if (Number.isInteger(num)) {
                  return num.toFixed(2) // 1000 -> 1000.00
                }

                // 1) округлюємо до 6 знаків
                let rounded = num.toFixed(6)

                // 2) прибираємо зайві нулі
                rounded = rounded.replace(/\.?0+$/, '')

                rounded = rounded.replace(/(\.\d*?[1-9])0+$/, '$1') // прибираємо кінцеві нулі
                rounded = rounded.replace(/\.([0-9])$/, '.$10') // .1 → .10

                return rounded
              }

              // тултіп
              let tooltipEl = document.getElementById('custom-tooltip')

              // створюємо якщо ще немає
              if (!tooltipEl) {
                tooltipEl = document.createElement('div')
                tooltipEl.id = 'custom-tooltip'
                tooltipEl.style.position = 'absolute'
                tooltipEl.style.background = 'rgba(46, 49, 56, .95)'
                tooltipEl.style.color = '#fff'
                tooltipEl.style.padding = '10px 14px'
                tooltipEl.style.borderRadius = '8px'
                tooltipEl.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)'
                tooltipEl.style.whiteSpace = 'pre-line'
                tooltipEl.style.pointerEvents = 'none'
                tooltipEl.style.zIndex = 999
                document.body.appendChild(tooltipEl)
              }

              // приховування tooltip
              if (tooltip.opacity === 0) {
                tooltipEl.style.opacity = 0
                return
              }

              // дані
              const chart = this._chart
              const data = chart.config.data.datasets[0].data
              const labels = chart.config.data.labels

              const index = tooltip.dataPoints[0].index
              const label = labels[index]
              const value = formatAmount(data[index])

              const balance = chart.config.data.allowedBalances[index]
              const tooltipExtends = `${i18n.white_wallet()} ${formatAmount(balance.white)}\n` +
                `${i18n.gray_wallet()}  ${formatAmount(balance.grey)}`

              // КАСТОМНИЙ ТЕКСТ
              const tooltipText =
                `${label}\n` +
                `${value}\n\n${tooltipExtends}`

              tooltipEl.textContent = tooltipText

              // позиціонування
              const position = chart.canvas.getBoundingClientRect()

              tooltipEl.style.opacity = 1
              tooltipEl.style.left =
                position.left + tooltip.caretX + 'px'
              tooltipEl.style.top =
                position.top + tooltip.caretY + 'px'
            }
          }
        }
      }
    },
    computed: {
      ...mapGetters([
        vuexTypes.accountBalances
      ]),
      allowedBalances () {
        const balances = this.accountBalances
        return PREFERRED_ASSET_CODES.reduce((result, code) => {
          const white = Math.max(0, Number(balances[code] && balances[code].balance) || 0)
          const grey = Math.max(0, Number(balances[`${code}D`] && balances[`${code}D`].balance) || 0)

          if (white + grey > 0) {
            result.push({
              asset: code,
              white,
              grey,
              allBalances: white + grey
            })
          }
          return result
        }, [])
      },

      chartData () {
        const rawLabels = this.allowedBalances.map((el) => formatTokenCode(el.asset))
        const rawData = this.allowedBalances.map((el) => Number(el.allBalances))

        return {
          labels: this.addPercentToLabels(rawLabels, rawData),
          datasets: [
            {
              backgroundColor: this.generateColors(this.allowedBalances.map(el => el.asset)),
              data: rawData
            }
          ],
          centerText: this.roundCurrency2(this.allowedBalances.reduce((acc, cur) => {
            return acc + (+cur.allBalances || 0)
          }, 0)),
          allowedBalances: this.allowedBalances
        }
      },
      legendItems () {
        const data = this.chartData
        return this.allowedBalances.map((balance, index) => ({
          asset: balance.asset,
          index,
          label: data.labels[index],
          color: data.datasets[0].backgroundColor[index]
        }))
      }
    },
    methods: {
      addPercentToLabels (labels, data) {
        const total = data.reduce((a, b) => a + b, 0)
        return labels.map((label, i) => {
          const percent = ((data[i] / total) * 100).toFixed(1)
          return `${label} ${percent}%`
        })
      },
      generateColors (codes) {
        const colors = ['#37A875', '#208B89', '#1870A6', '#D4A33B', '#C87543', '#A76389']
        return codes.map(code => colors[PREFERRED_ASSET_CODES.indexOf(code)])
      },
      toggleSector (index) {
        const chart = this.$data._chart
        if (!chart) return
        const arc = chart.getDatasetMeta(0).data[index]
        if (!arc) return
        arc.hidden = !arc.hidden
        this.$set(this.hiddenLegendItems, index, arc.hidden)
        chart.update()
      }
    },
    mounted () {
      if (this.allowedBalances.length) this.renderChart(this.chartData, this.chartOptions)
    },
    watch: {
      chartData () {
        this.$nextTick(() => {
          this.hiddenLegendItems = {}
          if (this.allowedBalances.length) {
            this.renderChart(this.chartData, this.chartOptions)
          } else if (this.$data._chart) {
            this.$data._chart.destroy()
            this.$data._chart = null
          }
        })
      }
    }
  }
</script>

<style lang="scss" scoped>
.crypto-chart-widget {
  position: relative;
  height: 100%;

  &__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    gap: 20px;

    .md-icon {
      margin: 0;
      font-size: 80px !important;
      margin-bottom: 20px;
    }

    p {
      font-size: 16px;
      text-align: center;
      font-weight: 500;
    }
  }

  &__container {
    padding-top: 20px;
    height: calc(100% - 25px);
    display: flex;
    flex-direction: column;
    min-height: 0;
  }

  &__plot {
    position: relative;
    flex: 1;
    min-height: 0;
  }

  &__legend {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 8px 4px;
    padding-top: 12px;
  }

  &__legend-item {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 5px;
    min-width: 0;
    padding: 0;
    border: 0;
    background: none;
    color: #000;
    font: inherit;
    font-size: 16px;
    line-height: 1.25;
    text-align: left;
    cursor: pointer;

    &.is-hidden {
      opacity: .45;
    }
  }

  &__legend-color {
    flex: 0 0 10px;
    width: 10px;
    height: 10px;
    border-radius: 2px;
  }

  @media (max-width: 767px) {
    &__legend {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/vue/app/dashboard/widgets/CryptoChart.vue